import { environment } from '@/environments/environment';
import { BriefResponseDto } from '../../../../common/general-types/briefs.dto';
import { CreateBriefPaymentMethodDto, PaymentMethodTypeEnum } from '../../../../common/general-types/shout-plus.dto';
import { getResolvedPaymentMethodType, isShoutPlusBrief } from '../../../../shout-plus/utils/shout-plus-hire.util';

/**
 * The money side of a hire, in one place.
 *
 * Three surfaces quote the same figure — the Spend cell in the Assignment table, the
 * Review application modal, and the toast after hiring — and they were computing it
 * three different ways. Worse, the table added a VAT-inclusive primary fee to
 * VAT-exclusive cross-posts, so on a GBP brief the row and the modal disagreed on what
 * the brand was about to spend.
 *
 * Everything here is a pure function over already-resolved net figures, so it can be
 * tested without Angular and reused by whoever needs the number next.
 *
 * All amounts are major units (pounds, not pence) — that is what
 * `PricingModelV2Service` hands back.
 */

/** UK VAT. The only rate the platform charges. */
export const VAT_RATE = 0.2;

/** The brief-level inputs the VAT rule needs. Kept structural so tests need no fixtures. */
type VatContext = Pick<BriefResponseDto, 'currentPrice' | 'businessAccount'> &
  Parameters<typeof isShoutPlusBrief>[0] &
  Pick<BriefResponseDto, 'brief_payment_method' | 'briefPaymentMethod'>;

/**
 * Whether this brief's spend is VAT-bearing.
 *
 * GBP only, and never for the Shout agency's own briefs or a SHOUT+ cash brief (where
 * the brand has already paid a fixed amount). Mirrors `applyBriefVat` on the API — if
 * one moves, both must.
 */
export function shouldApplyVat(
  brief: VatContext | null | undefined,
  paymentMethod?: CreateBriefPaymentMethodDto | null,
): boolean {
  if (!brief?.currentPrice || !brief.businessAccount) {
    return false;
  }

  if (
    isShoutPlusBrief(brief) &&
    getResolvedPaymentMethodType(brief as BriefResponseDto, paymentMethod) === PaymentMethodTypeEnum.CASH
  ) {
    return false;
  }

  if (String(brief.currentPrice.currencyCode ?? '').toUpperCase() !== 'GBP') {
    return false;
  }

  const noVatAgencyIds: string[] = [environment.shoutAgencyId];
  const agencyId = brief.businessAccount.agencyId;
  return !agencyId || !noVatAgencyIds.includes(agencyId);
}

/** VAT payable on a net figure, or 0 when the brief is exempt. */
export function vatFor(
  net: number,
  brief: VatContext | null | undefined,
  paymentMethod?: CreateBriefPaymentMethodDto | null,
): number {
  return shouldApplyVat(brief, paymentMethod) ? net * VAT_RATE : 0;
}

/** A net figure as the brand will actually be charged it. */
export function applyVat(
  net: number,
  brief: VatContext | null | undefined,
  paymentMethod?: CreateBriefPaymentMethodDto | null,
): number {
  return net + vatFor(net, brief, paymentMethod);
}

/**
 * Total net cost of a set of cross-posts.
 *
 * Deliberately not multiplied by `videosPerCreator`: a cross-post is one repost of an
 * asset already produced and paid for under the primary fee, which is how the API
 * prices it too (`calculateCrossChannelSpend`).
 */
export function crossPostNetTotal(prices: readonly number[]): number {
  return prices.reduce((total, price) => total + (Number.isFinite(price) ? price : 0), 0);
}

export interface HireCostBreakdown {
  /** Primary post fee, excluding VAT. */
  primaryNet: number;
  /** Every included cross-post, excluding VAT. */
  crossNet: number;
  /** Primary plus cross-posts, excluding VAT. */
  net: number;
  /** VAT on the whole hire; 0 when the brief is exempt. */
  vat: number;
  /** What gets committed against the wallet. */
  total: number;
  /** Primary fee as charged, for the "£X primary + £Y across N networks" sub-line. */
  primaryGross: number;
  /** Cross-posts as charged. */
  crossGross: number;
}

/**
 * The full cost of a hire, from the two net figures the caller already has.
 *
 * VAT is charged on the whole hire rather than per line, so the breakdown's line rows
 * can stay at the creator-facing rate and still sum to the total.
 */
export function hireCostBreakdown(input: {
  primaryNet: number;
  crossNet: number;
  brief: VatContext | null | undefined;
  paymentMethod?: CreateBriefPaymentMethodDto | null;
}): HireCostBreakdown {
  const primaryNet = Number.isFinite(input.primaryNet) ? input.primaryNet : 0;
  const crossNet = Number.isFinite(input.crossNet) ? input.crossNet : 0;
  const net = primaryNet + crossNet;
  const vat = vatFor(net, input.brief, input.paymentMethod);

  return {
    primaryNet,
    crossNet,
    net,
    vat,
    total: net + vat,
    primaryGross: applyVat(primaryNet, input.brief, input.paymentMethod),
    crossGross: applyVat(crossNet, input.brief, input.paymentMethod),
  };
}
